// Awake member token handling.
// Tokens are URL-safe base64-encoded JSON. NOT signed — this is a "friendly
// fence" while Memberstack SSO is being wired up. Threat model is low.
//
// Payload shape:
//   { v: 1, e: "member@example.com", p: "prc_...", iat: ..., exp: ... }
//
// Lifetime: 24h from iat, NOT single-use. Persist to sessionStorage so refresh +
// same-tab navigation keep working; new tab/window = fresh /dashboard visit.

const SESSION_KEY = "awake_member";
const QUALIFYING_PLANS = new Set([
  "prc_1-annual-n2170ed3",
  "prc_1-club-monthly-txnv03iu",
]);

export const AWAKE_DASHBOARD_URL = "https://awake.wearetheweave.co.uk/dashboard";

function urlSafeB64Decode(s) {
  // Add padding back, swap url-safe chars
  let str = s.replace(/-/g, "+").replace(/_/g, "/");
  while (str.length % 4) str += "=";
  return atob(str);
}

export function validateClaims(claims) {
  if (!claims || typeof claims !== "object") return false;
  if (claims.v !== 1) return false;
  if (typeof claims.e !== "string" || !claims.e.includes("@")) return false;
  if (typeof claims.p !== "string" || !QUALIFYING_PLANS.has(claims.p)) return false;
  const nowSec = Math.floor(Date.now() / 1000);
  if (typeof claims.exp !== "number" || claims.exp <= nowSec) return false;
  return true;
}

export function decodeToken(token) {
  try {
    const json = urlSafeB64Decode(token);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function readStoredMember() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const claims = JSON.parse(raw);
    if (!validateClaims(claims)) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    return claims;
  } catch {
    return null;
  }
}

/**
 * Reads ?wm=... from current URL. If present and valid, persists to sessionStorage
 * and returns the claims. Otherwise returns whatever is already in sessionStorage
 * (still validity-checked).
 * Also strips the ?wm param from the URL so it doesn't leak via copy-paste.
 */
export function ingestWmFromUrl() {
  try {
    const url = new URL(window.location.href);
    const token = url.searchParams.get("wm");
    if (token) {
      const claims = decodeToken(token);
      if (validateClaims(claims)) {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(claims));
        // Strip the param so users don't accidentally share an authed link
        url.searchParams.delete("wm");
        const cleanUrl = url.pathname + (url.search ? url.search : "") + url.hash;
        window.history.replaceState({}, "", cleanUrl);
        return claims;
      }
    }
  } catch {
    // ignore
  }
  return readStoredMember();
}

export function clearStoredMember() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}
